import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import styles from './EditMerchantGroup.module.scss';
import { Loading } from '..';
import { updateMerchantGroupApi, getAllMerchant } from '../../api/merchant';
import MerchantSelect from '../MerchantSelect/MerchantSelect';
import { MERCHANT_GROUP_FORM_FIELDS as FORM_FIELDS } from '../../constants/formFields';
import { toast } from 'react-toastify';

const FormField = ({ field, value, onChange, validated }) => {
  const { name, label, type, placeholder, validationMessage, rows } = field;
  return (
    <Form.Group as={Row} className='mb-3'>
      <Form.Label column sm={3}>
        {label}
      </Form.Label>
      <Col sm={9}>
        <Form.Control
          as={type === 'textarea' ? type : 'input'}
          type={type !== 'textarea' ? type : undefined}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          disabled={name === 'merchantGroupId'}
          required={false}
          isInvalid={false}
        />
        <Form.Control.Feedback type='invalid'>
          {validationMessage}
        </Form.Control.Feedback>
      </Col>
    </Form.Group>
  );
};

// EditMerchantGroupModal component
const EditMerchantGroupModal = ({
  show,
  onHide,
  groupData,
  token,
  onSuccess,
}) => {
  const [state, setState] = useState({
    merchantGroupId: '',
    groupName: '',
    description: '',
    imageUrl: '',
    merchantIds: [],
    loading: false,
    validated: false,
    merchants: [],
    merchantsLoading: false,
  });

  useEffect(() => {
    if (groupData) {
      // Pre-fill form fields with existing group data
      setState((prev) => ({
        ...prev,
        merchantGroupId: groupData.id || '',
        groupName: groupData.name || '',
        description: groupData.description || '',
        imageUrl: groupData.imageUrl || '',
        merchantIds:
          groupData.merchants?.map((m) => m.id) || groupData.merchantIds || [],
      }));
    }
  }, [groupData]);

  useEffect(() => {
    const fetchMerchants = async () => {
      if (show && token) {
        setState((prev) => ({ ...prev, merchantsLoading: true }));
        try {
          const response = await getAllMerchant(token);
          if (response) {
            // Normalize the response to handle different API response formats
            const merchantList = Array.isArray(response.data)
              ? response.data
              : Array.isArray(response)
              ? response
              : response?.items || [];
            setState((prev) => ({
              ...prev,
              merchants: merchantList,
              merchantsLoading: false,
            }));
          }
        } catch (error) {
          toast.error('Failed to load merchants');
        }
        setState((prev) => ({ ...prev, merchantsLoading: false }));
      }
    };

    fetchMerchants();
  }, [show, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const validations = [
      {
        check: () => state.merchantIds.length > 0,
        error: 'Please select at least one merchant',
      },
    ];

    const failed = validations.find((v) => !v.check());
    if (failed) {
      toast.error(failed.error);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const payload = {
        name: state.groupName,
        description: state.description,
        merchantIds: state.merchantIds,
      };

      const response = await updateMerchantGroupApi(
        payload,
        token,
        groupData.id
      );
      if (response) {
        onSuccess();
        toast.success('Merchant group updated successfully!');
        onHide();
        setState({
          merchantGroupId: '',
          groupName: '',
          description: '',
          imageUrl: '',
          merchantIds: [],
          loading: false,
          validated: false,
        });
      } else {
        const errorMessage = 'Failed to update merchant group';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Failed to update merchant group');
    }
    setState((prev) => ({ ...prev, loading: false }));
  };

  return (
    <Modal
      className={styles.editMerchantGroupModal}
      centered
      show={show}
      onHide={onHide}
      size='lg'
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Merchant Group</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate validated={state.validated}>
          {/* Render form fields except imageUrl */}
          {FORM_FIELDS.map(
            (field) =>
              field.name !== 'imageUrl' && (
                <FormField
                  key={field.name}
                  field={field}
                  value={state[field.name]}
                  onChange={handleInputChange}
                  validated={state.validated}
                />
              )
          )}

          {/* Render merchant selection */}
          <MerchantSelect
            merchants={state.merchants}
            selectedMerchants={state.merchantIds}
            onMerchantSelect={(ids) =>
              setState((prev) => ({ ...prev, merchantIds: ids }))
            }
            loading={state.merchantsLoading}
            validated={state.validated}
          />
        </Form>

        {state.loading && <Loading />}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={state.loading}
        >
          {state.loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditMerchantGroupModal;
